import { useCallback, useEffect, useMemo, useRef } from "react";

interface UseTimeoutReturn {
  /**
   * Sets a timeout with the specified callback and delay.
   * - If a timeout is already set, it clears the existing one before setting a new one.
   */
  set: (method: VoidFunction, delay: number) => void;

  /**
   * Clears the currently active timeout, if any.
   */
  clear: () => void;

  /**
   * Immediately executes the timeout callback if it exists and clears the timeout.
   */
  flush: () => void;
}

/**
 * A custom hook that provides a timeout utility with `set`, `clear`, and `flush` methods.
 * It ensures proper cleanup and allows manual control over the timeout.
 *
 * @returns An object containing:
 * - `set`: A function to set a timeout.
 * - `clear`: A function to clear the timeout.
 * - `flush`: A function to immediately execute the timeout callback if it exists.
 */
export function useTimeout(): UseTimeoutReturn {
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callback = useRef<VoidFunction | null>(null);

  /**
   * Sets a timeout with the specified callback and delay.
   * If a timeout is already set, it clears the existing one before setting a new one.
   *
   * @param {VoidFunction} method - The callback function to execute after the delay.
   * @param {number} delay - The delay in milliseconds.
   */
  const set = useCallback((method: VoidFunction, delay: number) => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    callback.current = method;
    timeout.current = setTimeout(() => {
      callback.current?.();
      callback.current = null;
    }, delay);
  }, []);

  /**
   * Clears the currently active timeout, if any, and resets the callback.
   */
  const clear = useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
      callback.current = null;
    }
  }, []);

  /**
   * Immediately executes the timeout callback if it exists and clears the timeout.
   */
  const flush = useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
      callback.current?.();
      callback.current = null;
    }
  }, []);

  useEffect(() => {
    // Cleanup on component unmount.
    return () => {
      clearTimeout(timeout.current!);
      timeout.current = null;
      callback.current = null;
    };
  }, []);

  return useMemo(() => ({ set, clear, flush }), [set, clear, flush]);
}
